import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MailWarning, ShoppingBag, Heart, Receipt, LayoutDashboard, Shield, LogOut, Bell } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

function ProfileForm() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ name: user.name, bio: user.bio || "", country: user.country || "Nigeria" });
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put("/users/me", f); setUser({ ...user, ...data }); toast.success("Profile updated"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="card-surface space-y-4 p-6">
      <p className="font-semibold">Personal details</p>
      <div className="space-y-2"><Label>Name</Label><Input data-testid="profile-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
      <div className="space-y-2"><Label>Country</Label><Input data-testid="profile-country-input" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} /></div>
      <div className="space-y-2"><Label>Bio</Label><Textarea data-testid="profile-bio-input" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} /></div>
      <Button type="submit" data-testid="profile-save-btn">Save changes</Button>
    </form>
  );
}

function PasswordForm() {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/users/me/password", f); toast.success("Password changed"); setF({ current_password: "", new_password: "" }); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="card-surface space-y-4 p-6">
      <p className="font-semibold">Change password</p>
      <Input data-testid="current-password-input" type="password" placeholder="Current password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} required />
      <Input data-testid="new-password-input" type="password" placeholder="New password (min 8)" minLength={8} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} required />
      <Button type="submit" variant="secondary" data-testid="change-password-btn">Update password</Button>
    </form>
  );
}

export default function Profile() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const links = [
    ["/purchases", ShoppingBag, "My purchases"], ["/favorites", Heart, "My favorites"], ["/orders", Receipt, "Order history"], ["/notifications", Bell, "Notifications"],
    user.is_seller ? ["/seller", LayoutDashboard, "Seller dashboard"] : ["/seller", LayoutDashboard, "Become a seller"],
    ...(user.role === "admin" ? [["/admin", Shield, "Admin dashboard"]] : []),
  ];
  const resend = async () => { try { await api.post("/auth/resend-verification"); toast.success("Verification email sent"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <PageHeader eyebrow="Account" title={`Hi, ${user.name.split(" ")[0]}`} />
      {!user.email_verified && (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm" data-testid="verify-email-banner">
          <MailWarning size={18} className="text-amber-500" /><span className="flex-1">Please verify your email address ({user.email}).</span>
          <Button size="sm" variant="outline" onClick={resend} data-testid="resend-verification-btn">Resend email</Button>
        </div>
      )}
      <div className="grid gap-6 md:grid-cols-[260px_1fr]">
        <div className="card-surface h-fit p-2">
          {links.map(([to, I, l]) => (
            <Link key={l} to={to} data-testid={`profile-link-${l.split(" ").pop().toLowerCase()}`} className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm hover:bg-secondary"><I size={16} />{l}</Link>
          ))}
          <button onClick={async () => { await logout(); nav("/"); }} data-testid="profile-logout-btn" className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm text-rose-500 hover:bg-secondary"><LogOut size={16} />Log out</button>
        </div>
        <div className="space-y-6"><ProfileForm /><PasswordForm /></div>
      </div>
    </div>
  );
}
